import { readIds, readHandles, STORAGE_KEY } from 'wishlist';

class WishlistPage extends HTMLElement {
  connectedCallback() {
    this.listeners?.abort();
    this.listeners = new AbortController();
    this.cards = new Map();
    this.list = this.querySelector('[data-wishlist-products]');
    this.status = this.querySelector('[data-wishlist-status]');
    const options = { signal: this.listeners.signal };
    document.addEventListener('wishlist:change', () => this.load(), options);
    window.addEventListener('storage', (event) => { if (event.key === STORAGE_KEY) this.load(); }, options);
    this.querySelector('[data-wishlist-retry]').addEventListener('click', () => this.load(), options);
    this.load();
  }

  disconnectedCallback() {
    this.listeners.abort();
    this.request?.abort();
  }

  async fetchCards(url, signal) {
    url.searchParams.set('section_id', 'wishlist-products');
    const response = await fetch(url, { signal });
    if (response.status === 404) return [];
    if (!response.ok) throw new Error('Wishlist request failed');
    const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
    if (!doc.querySelector('#shopify-section-wishlist-products')) throw new Error('Wishlist section unavailable');
    return Array.from(doc.querySelectorAll('[data-wishlist-product]'));
  }

  async load() {
    this.request?.abort();
    const request = this.request = new AbortController();
    const ids = readIds().reverse();
    this.querySelector('[data-wishlist-error]').hidden = true;
    this.querySelector('[data-wishlist-empty]').hidden = ids.length !== 0;
    this.status.hidden = !ids.length;
    this.status.textContent = 'Loading your favourites…';
    // Remove unsaved cards immediately, including while a previous fetch is pending.
    for (const child of Array.from(this.list.children)) if (!ids.includes(child.dataset.wishlistProduct)) child.remove();
    if (!ids.length) { this.list.removeAttribute('aria-busy'); return; }
    this.list.setAttribute('aria-busy', 'true');
    try {
      const missing = ids.filter((id) => !this.cards.has(id));
      for (let offset = 0; offset < missing.length; offset += 20) {
        const batch = missing.slice(offset, offset + 20);
        const url = new URL(this.getAttribute('search-url'), location.origin);
        url.searchParams.set('type', 'product');
        url.searchParams.set('options[unavailable_products]', 'show');
        url.searchParams.set('q', batch.map((id) => `id:${id}`).join(' OR '));
        const cards = await this.fetchCards(url, request.signal);
        cards.forEach((card) => { if (batch.includes(card.dataset.wishlistProduct)) this.cards.set(card.dataset.wishlistProduct, card); });
      }
      // Unlisted products may not appear in search. Saved handles let their PDP
      // render the same card without downloading the catalogue or a full page.
      const handles = { ...JSON.parse(this.querySelector('[data-wishlist-known-handles]').textContent), ...readHandles() };
      for (const id of missing.filter((id) => !this.cards.has(id))) {
        if (!/^[a-z0-9-]+$/.test(handles[id] || '')) continue;
        const root = this.getAttribute('root-url').replace(/\/$/, '');
        const url = new URL(`${root}/products/${handles[id]}`, location.origin);
        const cards = await this.fetchCards(url, request.signal);
        const card = cards.find((card) => card.dataset.wishlistProduct === id);
        if (card) this.cards.set(id, card);
      }
      if (request.signal.aborted) return;
      this.list.replaceChildren(...ids.map((id) => this.cards.get(id) || this.unavailableCard(id)));
      this.status.textContent = `${ids.length} saved ${ids.length === 1 ? 'product' : 'products'}`;
    } catch (error) {
      if (error.name !== 'AbortError') {
        this.status.hidden = true;
        this.querySelector('[data-wishlist-error]').hidden = false;
      }
    } finally {
      if (!request.signal.aborted) this.list.removeAttribute('aria-busy');
    }
  }

  unavailableCard(id) {
    const card = document.createElement('div');
    card.dataset.wishlistProduct = id;
    card.className = 'wishlist-page__unavailable v-stack gap-4';
    card.innerHTML = '<p>This saved product is no longer available.</p><wishlist-button add-label="Save product" remove-label="Remove from wishlist"><button type="button" class="link">Remove from wishlist</button></wishlist-button>';
    card.querySelector('wishlist-button').setAttribute('product-id', id);
    return card;
  }
}

if (!customElements.get('wishlist-page')) customElements.define('wishlist-page', WishlistPage);
